<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { publicTrialsApi } from '../../api/publicTrials';
import { publicBillingSetupApi } from '../../api/publicBillingSetup';

const route = useRoute();
const trialToken = computed(() => route.params.token);

const status = ref(null);
const loadingStatus = ref(true);
const submitting = ref(false);
const errorMessage = ref(null);
const result = ref(null);

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: 897,
    interactions: 3000,
    inboxes: 2,
    users: 5,
    digital_humans: 1,
    highlight: false,
    bullets: [
      '3.000 interacoes IA por mes',
      '2 caixas de entrada',
      '5 usuarios',
      '1 Humano Digital',
      '50MB de RAG',
      'WhatsApp Meta (2 numeros)',
    ],
  },
  {
    key: 'growth',
    name: 'Growth',
    price: 1697,
    interactions: 8000,
    inboxes: 5,
    users: 10,
    digital_humans: 2,
    highlight: true,
    bullets: [
      '8.000 interacoes IA por mes',
      '5 caixas de entrada',
      '10 usuarios',
      '2 Humanos Digitais',
      '200MB de RAG',
      'WhatsApp Meta (5 numeros)',
      'Suporte prioritario',
    ],
  },
  {
    key: 'scale',
    name: 'Scale',
    price: 3197,
    interactions: 20000,
    inboxes: 10,
    users: 20,
    digital_humans: 5,
    highlight: false,
    bullets: [
      '20.000 interacoes IA por mes',
      '10 caixas de entrada',
      '20 usuarios',
      '5 Humanos Digitais',
      '500MB de RAG',
      'WhatsApp Meta (10 numeros)',
      'API + MCP access',
    ],
  },
];

const form = ref({
  tier: 'starter',
  billing_type: 'BOLETO',
  cpf_cnpj: '',
});

const sanitizedCpf = computed(() => form.value.cpf_cnpj.replace(/\D/g, ''));
const cpfValid = computed(() => [11, 14].includes(sanitizedCpf.value.length));

const canSubmit = computed(
  () => !!form.value.tier && cpfValid.value && !submitting.value
);

const formatCpf = e => {
  let val = e.target.value.replace(/\D/g, '').slice(0, 14);
  if (val.length <= 11) {
    val = val
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1-$2');
  } else {
    val = val
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  form.value.cpf_cnpj = val;
};

const fetchStatus = async () => {
  try {
    const { data } = await publicTrialsApi.getStatus(trialToken.value);
    status.value = data;
    errorMessage.value = null;
  } catch (e) {
    if (e?.response?.status === 404) {
      errorMessage.value = 'Trial nao encontrado. Confira o link.';
    }
    // silencia outros erros: o form de upgrade funciona sem o status
  } finally {
    loadingStatus.value = false;
  }
};

const handleSubmit = async () => {
  if (!canSubmit.value) return;
  submitting.value = true;
  errorMessage.value = null;
  try {
    const { data } = await publicBillingSetupApi.submit(trialToken.value, {
      tier: form.value.tier,
      billing_type: form.value.billing_type,
      cpf_cnpj: sanitizedCpf.value,
    });
    result.value = data;
  } catch (e) {
    errorMessage.value =
      e?.response?.data?.error ||
      'Erro ao processar pagamento. Confira os dados e tente de novo.';
  } finally {
    submitting.value = false;
  }
};

const formatPrice = price => `R$ ${price.toLocaleString('pt-BR')}`;

onMounted(fetchStatus);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="min-h-screen bg-n-background text-n-slate-12">
    <div v-if="loadingStatus" class="text-center py-32 text-n-slate-10">
      Carregando...
    </div>

    <div v-else-if="result" class="max-w-xl mx-auto px-6 py-20 text-center">
      <div class="bg-n-solid-1 rounded-2xl border border-n-brand p-8">
        <div class="mb-6">
          <div
            class="size-16 mx-auto rounded-full bg-n-brand text-white flex items-center justify-center text-3xl mb-4"
          >
            ✓
          </div>
          <h1 class="text-2xl font-bold text-n-slate-12 mb-2">
            Cobranca criada!
          </h1>
          <p class="text-n-slate-11">
            Plano <strong>{{ result.tier }}</strong> ·
            {{ formatPrice(result.monthly_price_brl) }}/mes
          </p>
        </div>

        <div v-if="result.payment_link" class="mb-4">
          <a
            :href="result.payment_link"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-full py-3 rounded-lg font-semibold text-white bg-n-brand hover:opacity-90"
          >
            Pagar agora →
          </a>
          <p class="text-xs text-n-slate-10 mt-3">
            Voce sera redirecionado pra pagina segura do Asaas. Apos confirmar,
            sua conta sera ativada automaticamente.
          </p>
        </div>

        <div
          v-else
          class="text-sm text-n-amber-11 bg-n-amber-3 border border-n-amber-6 rounded-lg p-3"
        >
          Cobranca foi criada mas o link de pagamento ainda nao esta disponivel.
          Voce recebera um email com as instrucoes em alguns minutos.
        </div>
      </div>
    </div>

    <div v-else class="max-w-5xl mx-auto px-6 py-12">
      <div class="text-center mb-10">
        <h1 class="text-3xl md:text-4xl font-bold text-n-slate-12 mb-2">
          Escolha seu plano
        </h1>
        <p class="text-n-slate-11">
          Voce pode mudar ou cancelar a qualquer momento.
        </p>
      </div>

      <!-- Plan cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <button
          v-for="plan in PLANS"
          :key="plan.key"
          type="button"
          class="text-left p-6 rounded-2xl border-2 transition-all"
          :class="
            form.tier === plan.key
              ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/30'
              : 'border-n-weak bg-n-solid-1 hover:border-n-brand/50'
          "
          @click="form.tier = plan.key"
        >
          <div
            v-if="plan.highlight"
            class="text-xs font-semibold uppercase tracking-wider text-n-brand mb-1"
          >
            ★ Mais popular
          </div>
          <div class="text-xl font-bold text-n-slate-12 mb-1">
            {{ plan.name }}
          </div>
          <div class="text-3xl font-bold text-n-slate-12 mb-1">
            {{ formatPrice(plan.price)
            }}<span class="text-sm font-normal text-n-slate-10">/mes</span>
          </div>
          <div class="text-xs text-n-slate-10 mb-4">
            {{ plan.interactions.toLocaleString('pt-BR') }} interacoes IA
          </div>
          <ul class="space-y-2">
            <li
              v-for="b in plan.bullets"
              :key="b"
              class="text-sm text-n-slate-11 flex items-start gap-2"
            >
              <span class="text-n-brand mt-0.5">✓</span>
              <span>{{ b }}</span>
            </li>
          </ul>
        </button>
      </div>

      <!-- Form -->
      <form
        class="bg-n-solid-1 rounded-2xl border border-n-weak p-6 max-w-xl mx-auto space-y-4"
        @submit.prevent="handleSubmit"
      >
        <h2 class="text-lg font-semibold text-n-slate-12 mb-2">
          Dados de cobranca
        </h2>

        <div>
          <label
            for="cpf-input"
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
          >
            CPF ou CNPJ *
          </label>
          <input
            id="cpf-input"
            :value="form.cpf_cnpj"
            type="text"
            placeholder="000.000.000-00"
            class="w-full px-3 py-2.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
            required
            @input="formatCpf"
          />
          <p
            v-if="form.cpf_cnpj && !cpfValid"
            class="text-xs text-n-ruby-11 mt-1"
          >
            CPF/CNPJ invalido
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            Forma de pagamento *
          </label>
          <div class="grid grid-cols-3 gap-2">
            <button
              v-for="opt in ['BOLETO', 'PIX', 'CREDIT_CARD']"
              :key="opt"
              type="button"
              class="py-2 px-3 rounded-lg border text-sm font-medium transition"
              :class="
                form.billing_type === opt
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11 hover:border-n-brand/50'
              "
              @click="form.billing_type = opt"
            >
              {{
                opt === 'CREDIT_CARD'
                  ? 'Cartao'
                  : opt === 'BOLETO'
                    ? 'Boleto'
                    : 'PIX'
              }}
            </button>
          </div>
        </div>

        <div
          v-if="errorMessage"
          class="text-sm text-n-ruby-11 bg-n-ruby-3 border border-n-ruby-6 rounded-lg px-3 py-2"
        >
          {{ errorMessage }}
        </div>

        <button
          type="submit"
          :disabled="!canSubmit"
          class="w-full py-3 rounded-lg font-semibold text-white bg-n-brand hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ submitting ? 'Processando...' : 'Confirmar e gerar cobranca →' }}
        </button>

        <p class="text-xs text-n-slate-10 text-center">
          Pagamento processado via Asaas — provedor regulamentado pelo Banco
          Central.
        </p>
      </form>
    </div>
  </div>
</template>
